import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { paymentAPI } from '../services/api';
import { FiDollarSign, FiRefreshCw, FiCalendar, FiCheckCircle, FiClock, FiXCircle, FiMail, FiBook, FiDownload } from 'react-icons/fi';

const PaymentHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    from: Math.floor(Date.now() / 1000) - (90 * 24 * 60 * 60), // Last 90 days
    to: Math.floor(Date.now() / 1000)
  });

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await paymentAPI.getPaymentHistory({
        from: dateRange.from,
        to: dateRange.to
      });
      
      if (response.data.success) {
        setHistory(response.data.data);
      } else {
        toast.error(response.data.message || 'Failed to fetch payment history');
      }
    } catch (error) {
      toast.error('Error fetching payment history');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (days) => {
    const to = Math.floor(Date.now() / 1000);
    const from = to - (days * 24 * 60 * 60);
    setDateRange({ from, to });
  };

  const handleRefresh = () => {
    fetchHistory();
  };

  const handleExport = () => {
    const csvContent = [
      ['Payment ID', 'Customer Email', 'Course', 'Amount', 'Status', 'Method', 'Date'].join(','),
      ...filteredHistory.map(h => [
        h.payment_id || h.id,
        h.email,
        h.course_title || 'N/A',
        h.amount,
        h.status,
        h.method || 'online',
        new Date(h.created_at).toLocaleString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Payment history exported successfully');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      captured: { color: 'bg-green-100 text-green-800', icon: FiCheckCircle, label: 'Success' },
      success: { color: 'bg-green-100 text-green-800', icon: FiCheckCircle, label: 'Success' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: FiClock, label: 'Pending' },
      authorized: { color: 'bg-blue-100 text-blue-800', icon: FiClock, label: 'Authorized' },
      failed: { color: 'bg-red-100 text-red-800', icon: FiXCircle, label: 'Failed' }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: FiClock, label: status };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  const filteredHistory = history.filter(h => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (h.email && h.email.toLowerCase().includes(search)) ||
      (h.payment_id && h.payment_id.toLowerCase().includes(search)) ||
      (h.course_title && h.course_title.toLowerCase().includes(search))
    );
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment History</h1>
          <p className="text-gray-600 mt-1">Complete payment transaction history</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            disabled={loading || filteredHistory.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <FiDownload />
            Export CSV
          </button>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            <FiRefreshCw className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <FiCalendar className="text-gray-600" />
          <span className="text-sm text-gray-600 font-medium">Date Range:</span>
          <button
            onClick={() => { handleDateRangeChange(30); fetchHistory(); }}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Last 30 Days
          </button>
          <button
            onClick={() => { handleDateRangeChange(90); fetchHistory(); }}
            className="px-3 py-1 text-sm bg-primary-100 text-primary-700 rounded-lg"
          >
            Last 90 Days
          </button>
          <button
            onClick={() => { handleDateRangeChange(180); fetchHistory(); }}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Last 6 Months
          </button>
          <button
            onClick={() => { handleDateRangeChange(365); fetchHistory(); }}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Last Year
          </button>
        </div>

        <div>
          <input
            type="text"
            placeholder="Search by email, payment ID, or course..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
          />
        </div>
      </div>

      {/* History Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="text-center py-12">
            <FiDollarSign className="mx-auto text-5xl text-gray-400 mb-4" />
            <p className="text-gray-600">No payment history found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Course
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredHistory.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900">{item.payment_id || item.id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="flex items-center gap-1 text-gray-900">
                          <FiMail className="text-gray-400" />
                          {item.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FiBook className="text-gray-400" />
                        <span className="text-sm text-gray-900">{item.course_title || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        ₹{parseFloat(item.amount).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-green-600 font-medium">
                        ₹{parseFloat(item.institute_net_amount || item.amount).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-orange-600 font-medium">
                        ₹{parseFloat(item.institute_commission || 0).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(item.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(item.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
